import { TZDate } from '@date-fns/tz';
import { isValid, parseISO } from 'date-fns';
import { getTimezone } from './getCountry';

/**
 * True when the string already carries an explicit zone (trailing `Z` or a
 * `±HH:mm`/`±HHmm`/`±HH` offset). Defensive: only tests the last 10 chars to
 * avoid regex issues with long/malformed strings.
 */
const hasExplicitZone = (time: string): boolean => {
  const testStr = time.length > 10 ? time.slice(-10) : time;
  return /([zZ]|[+-]\d{2}(:?\d{2})?)$/.test(testStr);
};

/**
 * Shared parse core: applies the trailing-`Z` heuristic for naive UTC inputs
 * (only when the string has no explicit zone), tries `parseISO` first, then
 * falls back to the native `Date` constructor. Returns null when the value is
 * unparseable. Single source of truth for the parse + zone detection used by
 * both {@link parseTimeStamp} (falls back to now) and {@link parseTimeStampOrNull}
 * (returns null), so the two can never silently diverge.
 */
const parseToValidDate = (time: string, isUtc: boolean): Date | null => {
  const trailingZ = isUtc && !hasExplicitZone(time) ? 'Z' : '';
  const parsed = parseISO(time + trailingZ);
  const date = isValid(parsed) ? parsed : new Date(time + trailingZ);
  return isValid(date) ? date : null;
};

/**
 * Binds a parsed instant/wall-clock `Date` to the account timezone. For UTC
 * inputs the `Date` is a real instant, exposed via target-tz field readouts.
 * For naive local-time inputs (e.g. EHR appointment times) the wall fields were
 * parsed in the browser's local tz, so they are reinterpreted as being in
 * `timezone` — the underlying UTC instant is then correct for that wall time in
 * `timezone`.
 *
 * A browser can report a zone `Intl` itself rejects (`Etc/Unknown` when the OS
 * zone can't be mapped, seen on cloud-hosted Windows desktops). `TZDate` turns
 * that into an Invalid Date, which date-fns `format` throws on, so the parsed
 * date is returned unbound instead.
 */
const toAccountTZDate = (date: Date, isUtc: boolean, timezone: string): Date => {
  const zoned = isUtc
    ? new TZDate(date, timezone)
    : new TZDate(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds(),
        timezone
      );
  return isValid(zoned) ? zoned : date;
};

/**
 * IMPORTANT: Handle differently for EHR and Heidi sessions.
 * Parse a date/time into a Date object in the user's timezone.
 * @param time Date/ISO string. If string has an explicit zone (e.g., Z or ±HH:mm), that zone is preserved.
 * @param isUtc When true, interpret naive inputs as UTC; when false, interpret as local time (used for EHR local timestamps).
 */
export const parseTimeStamp = (time: string | null | undefined, isUtc = true): Date => {
  const timezone = getTimezone();
  const date = time ? parseToValidDate(time, isUtc) : null;
  if (!date) {
    return toAccountTZDate(new Date(), true, timezone);
  }
  return toAccountTZDate(date, isUtc, timezone);
};

/**
 * Like {@link parseTimeStamp} but returns null instead of falling back to the
 * current time when the input is missing or unparseable. Callers that must
 * distinguish "no valid date" (e.g. session-list date grouping) share the exact
 * parse + zone-detection logic, so they can never silently diverge from
 * `parseTimeStamp` (which would reintroduce the APP-7905 mis-grouping).
 */
export const parseTimeStampOrNull = (
  time: string | null | undefined,
  isUtc = true
): Date | null => {
  if (!time) {
    return null;
  }
  const date = parseToValidDate(time, isUtc);
  return date ? toAccountTZDate(date, isUtc, getTimezone()) : null;
};

// Moved to `@heidi/utils/dates`. Unlike `parseTimeStamp` and
// `parseTimeStampOrNull` — which read the account timezone via `getTimezone()`
// and so stay app-side — these two touch no locale, timezone or app state, so
// they are portable. Re-exported here so existing importers are untouched.
export { convertMomentToDateFnsFormat, safeToISOString } from './dates';
